import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import SettingsPageHeading from "../components/pages/SettingsPageHeading";
import {
  getEmailUpdates,
  getNotificationPreferences,
  updateEmailUpdates,
  updateNotificationPreferences,
} from "../api/user";
import {
  getPushPermission,
  isInApp,
  onPushPermissionChange,
  openPushSettings,
  requestPushPermission,
} from "../utils/pushPermission";

// Settings → Notifications (Mantis 13084 / 13085, locked visual). Saves on
// every flip; the header bell still opens the notification inbox.
// Email updates writes the same preference as /email-updates (Loops).

// Toast wording per toggle, same as Email updates ("Email updates saved.").
const LABELS = {
  activity: "Activity alerts",
  tips: "Tips & announcements",
  email: "Email updates",
};

// Same switch as Email updates and Page tips.
function Switch({ on, onClick, disabled, label }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="w-14 h-7 rounded-full flex items-center cursor-pointer transition-all shrink-0"
      style={{
        backgroundColor: on ? "#35b23c" : "#d3d3d3",
        justifyContent: on ? "flex-end" : "flex-start",
        padding: "3px",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div className="w-6 h-6 bg-white rounded-full shadow" />
    </div>
  );
}

function Row({ title, body, children }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="font-medium text-gray-800">{title}</p>
        <p className="mt-0.5 text-sm text-secondary">{body}</p>
      </div>
      {children}
    </div>
  );
}

// The line at the top of the card while this device can't receive push.
function PermissionLine({ state, onTurnOn, onOpenSettings }) {
  if (state === "prompt") {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="font-medium text-gray-800">Push is off on this device.</p>
        <button type="button" onClick={onTurnOn} className="font-semibold text-linkTeal">
          Turn On
        </button>
      </div>
    );
  }
  if (state === "blocked" && isInApp()) {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="font-medium text-gray-800">Notifications are off for QUSHi.</p>
        <button type="button" onClick={onOpenSettings} className="font-semibold text-linkTeal">
          Open Settings
        </button>
      </div>
    );
  }
  if (state === "blocked") {
    return (
      <p className="font-medium text-gray-800">
        Notifications are blocked in your browser. Turn them on in browser settings.
      </p>
    );
  }
  return null;
}

const NotificationSettings = () => {
  const [sidebarToggle] = useOutletContext();
  const [prefs, setPrefs] = useState({ activity: true, tips: false, email: true });
  const [loaded, setLoaded] = useState(false);
  const [permission, setPermission] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [pushRes, emailRes] = await Promise.all([
          getNotificationPreferences(),
          getEmailUpdates(),
        ]);
        setPrefs({
          activity: !!pushRes.data.activity,
          tips: !!pushRes.data.tips,
          email: !!emailRes.data.email_updates,
        });
      } catch (error) {
        console.log("Error fetching notification preferences:", error);
      } finally {
        setLoaded(true);
      }
    };
    load();
    getPushPermission().then(setPermission);

    // Back from phone or browser settings: re-read the permission.
    const recheck = () => {
      if (document.visibilityState === "visible") getPushPermission().then(setPermission);
    };
    document.addEventListener("visibilitychange", recheck);
    const unsubscribe = onPushPermissionChange(setPermission);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      unsubscribe();
    };
  }, []);

  const askPermission = async () => {
    const { state } = await requestPushPermission();
    setPermission(state);
  };

  const save = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      if (key === "email") {
        const res = await updateEmailUpdates({ email_updates: value });
        setPrefs((p) => ({ ...p, email: !!res.data.email_updates }));
      } else {
        const res = await updateNotificationPreferences({ [key]: value });
        setPrefs((p) => ({ ...p, activity: !!res.data.activity, tips: !!res.data.tips }));
      }
    } catch (error) {
      console.log("Update failed:", error);
      setPrefs((p) => ({ ...p, [key]: !value }));
      toast.error(`Could not save ${LABELS[key]}. Try again.`);
      return;
    }
    toast.success(`${LABELS[key]} saved.`);
    // Switching a push toggle ON is asking for push; switching OFF never prompts.
    if (value && key !== "email" && permission === "prompt") askPermission();
  };

  const line = (
    <PermissionLine
      state={permission}
      onTurnOn={askPermission}
      onOpenSettings={openPushSettings}
    />
  );
  const showLine = permission === "prompt" || permission === "blocked";

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      <PageHeaderTitle title={"Notifications"} showDesktopTitle={false} />

      <PageContainer>
        <div className="px-4 pt-4 md:pt-10 md:px-0 md:max-w-[55rem] md:mx-auto">
          <SettingsPageHeading
            title="Notifications"
            subtitle="Choose what QUSHi sends you."
            className="mb-3 md:mb-4"
          />
          <div className="md:rounded-2xl md:border md:border-gray-200 md:bg-white md:p-8">
            {showLine && (
              <div className="pb-5 mb-5 border-b border-gray-200">{line}</div>
            )}
            <div className="space-y-6">
              <Row
                title="Activity alerts"
                body="Order fills, price alerts, league results, messages, and comments."
              >
                <Switch
                  on={prefs.activity}
                  disabled={!loaded}
                  label="Activity alerts"
                  onClick={() => save("activity", !prefs.activity)}
                />
              </Row>
              <Row
                title="Tips & announcements"
                body="New leagues, trading tips, and QUSHi news."
              >
                <Switch
                  on={prefs.tips}
                  disabled={!loaded}
                  label="Tips & announcements"
                  onClick={() => save("tips", !prefs.tips)}
                />
              </Row>
              <Row
                title="Email updates"
                body="Trading tips, league announcements, and QUSHi updates."
              >
                <Switch
                  on={prefs.email}
                  disabled={!loaded}
                  label="Email updates"
                  onClick={() => save("email", !prefs.email)}
                />
              </Row>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200">
              <Row
                title="Account emails"
                body="Verification and security emails. Always on."
              />
            </div>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default NotificationSettings;
